import { Outlet, useLocation } from 'react-router-dom';
import { Box, Stack } from '@mui/material';

import { LanguageSelectV1 } from '~components/molecules';
import { NavbarWrapper } from '~components/molecules/navbar/NavbarWrapper';
import { LocalizationTypography } from '~components/organisms';
import { BAD_STATION_STATUS_PAGE_PATH } from '~configs';
import {
  useTenantBranding,
  useTenantDocumentTitle,
} from '~hooks/tenant/useTenantBranding';
import {
  EventTrackingProvider,
  MainProvider,
  StationPricingProvider,
  VenueCountryProvider,
} from '~providers';
import { inMemoryJWTService } from '~services';

export const MainLayout: React.FC = () => {
  return (
    <EventTrackingProvider>
      <MainProvider>
        <VenueCountryProvider>
          <StationPricingProvider>
            <MainLayoutContent />
          </StationPricingProvider>
        </VenueCountryProvider>
      </MainProvider>
    </EventTrackingProvider>
  );
};

const MainLayoutContent: React.FC = () => {
  const location = useLocation();
  const { isWhiteLabel, supportPhone } = useTenantBranding();
  useTenantDocumentTitle();
  const enableTextCustomerSupport = !location.pathname.includes(
    BAD_STATION_STATUS_PAGE_PATH,
  );
  const isAfterLoggedIn = !!inMemoryJWTService.getCurrentToken();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        minHeight: '100dvh',
        height: 'fit-content',
        maxWidth: '1024px',
        margin: 'auto',
      }}
    >
      {/* Navbar */}
      {isAfterLoggedIn && <NavbarWrapper />}

      {/* Outlet display */}
      <Stack
        sx={{
          justifyContent: 'space-between',
          flex: 1,
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <Stack
          sx={{
            flex: 1,
            flexGrow: 1,
          }}
        >
          <Outlet />
        </Stack>

        <Stack
          gap={1}
          alignItems="center"
          sx={{
            backgroundColor: '#F9FAFB',
            position: 'relative',
            alignItems: 'center',
            bottom: 0,
            paddingTop: 2,
            paddingBottom: 4,
          }}
        >
          {/* DISABLE TEXT SUPPORT ON BAD STATUS STATION PAGE */}
          {enableTextCustomerSupport && isAfterLoggedIn && supportPhone && (
            <Stack
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
                padding: 0.75,
              }}
            >
              <LocalizationTypography
                variant="Inter/Subheading-sm"
                textId="app.mainLayout.customerSupportTextHelp"
                defaultText="Need help?"
              />
              <Box
                component={'a'}
                href={`sms:${supportPhone}`}
                sx={{ textDecoration: 'unset' }}
              >
                <LocalizationTypography
                  variant="Inter/Subheading-sm"
                  color="brand.dark"
                  marginLeft={1}
                  textId={
                    isWhiteLabel
                      ? 'app.mainLayout.customerSupportTextPhone'
                      : 'app.mainLayout.customerSupportText'
                  }
                  textValues={{ phone: supportPhone }}
                  defaultText={`Text ${supportPhone}`}
                />
              </Box>
            </Stack>
          )}

          <LanguageSelectV1 />
        </Stack>
      </Stack>
    </Box>
  );
};
